import { pushApi, SendPushToUserRequest, SendPushToGroupRequest } from '../api/pushApi';
import { useBaseQuery, useBaseMutation, useInvalidateQueries } from './useBaseQuery';
import { PaginationData } from '../models/paginationData';
import {
    PushHistoryItem,
    PushSendResult,
    PushTargetPreview,
    PushTargetType,
} from '../types/push';

export const pushKeys = {
    all: ['push'] as const,
    histories: () => [...pushKeys.all, 'histories'] as const,
    history: (page: number, size: number) => [...pushKeys.histories(), { page, size }] as const,
    preview: (targetType: PushTargetType) => [...pushKeys.all, 'preview', targetType] as const,
};

export const usePushHistories = (page: number, size: number = 20) => {
    return useBaseQuery<PaginationData<PushHistoryItem>>(
        pushKeys.history(page, size),
        () => pushApi.getHistories(page, size),
        {
            staleTime: 30 * 1000,
        }
    );
};

export const usePushTargetPreview = (targetType: PushTargetType, enabled: boolean = true) => {
    return useBaseQuery<PushTargetPreview>(
        pushKeys.preview(targetType),
        () => pushApi.getTargetPreview(targetType),
        {
            enabled,
            staleTime: 60 * 1000,
        }
    );
};

export const useSendPushToUser = () => {
    const { invalidate } = useInvalidateQueries();
    return useBaseMutation<{ userId: number; data: SendPushToUserRequest }, PushSendResult>(
        ({ userId, data }) => pushApi.sendToUser(userId, data),
        {
            onSuccess: () => {
                invalidate(pushKeys.histories());
            },
        }
    );
};

export const useSendPushToGroup = () => {
    const { invalidate } = useInvalidateQueries();
    return useBaseMutation<SendPushToGroupRequest, PushSendResult>(
        (data) => pushApi.sendToGroup(data),
        {
            onSuccess: () => {
                invalidate(pushKeys.histories());
            },
        }
    );
};
